<script>
import zipcelx from 'zipcelx'

import getAgeFromDate from '@utils/age-from-date'
import Layout from '@layouts/blank.vue'

import chartMarketValue from '@components/charts/chart-market-value.vue'
import { mapActions } from 'vuex'
export default {
  page: {
    title: 'Market Value',
    meta: [{ name: 'description', content: 'The Market Value page.' }],
  },
  components: {
    Layout,
    chartMarketValue,
  },
  props: {
    playerId: {
      type: Number,
      default: null,
      required: true,
    },
  },
  data: function() {
    return {
      season: '2021/22',
      loaded: false,
      showPlayerNotFound: false,
      marketValueData: [],
      player: {},
    }
  },
  computed: {
    playerName() {
      if (this.player.name) {
        return this.player.name
      } else {
        return ''
      }
    },
  },
  async created() {
    // this.players = playerData
    this.loadData().then(() => {
      document.title = this.player.name
    })
  },
  methods: {
    ...mapActions('players', ['getSinglePlayerById']),
    ...mapActions('marketValue', ['getMarketValue']),
    downloadExcel() {
      const dateOptions = {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        timeZone: 'Europe/Rome',
      }
      const config = {
        filename: this.player.name,
        sheet: {
          data: [
            [
              {
                value: 'Date',
                type: 'string',
              },
              {
                value: 'Value',
                type: 'string',
              },
            ],
            ...this.marketValueData
              .filter((day) => {
                return new Date(day['@timestamp']).getHours() > 10
              })
              .map((day) => {
                const date = new Date(day['@timestamp'])
                const italianDate = date.toLocaleDateString(
                  'it-IT',
                  dateOptions
                )
                return [
                  {
                    value: italianDate,
                    type: 'string',
                  },
                  {
                    value: day.value,
                    type: 'number',
                  },
                ]
              }),
          ],
        },
      }
      zipcelx(config)
    },
    tableRowClass(key) {
      let classes = ''
      if (key === 'position') {
        classes += this.$style.posClass
      }
      if (key === 'club') {
        classes += this.$style.clubClass
      }
      if (key === 'contract') {
        classes += this.$style.contractClass
      }
      return classes
    },
    transformMarketValue(rawData) {
      if (rawData.length > 4) {
        rawData = rawData.slice(Math.max(rawData.length - 4, 1))
      }
      const data = rawData.map((timeValue) => {
        return {
          ...timeValue,
          time: {
            day: timeValue['@timestamp'].split('T')[0],
            hour: timeValue['@timestamp'].split('T')[1],
          },
        }
      })
      const today = new Date().toISOString().split('T')[0]
      let yesterday = new Date(today)

      yesterday.setDate(yesterday.getDate() - 1)
      yesterday = yesterday.toISOString().split('T')[0]

      const todayValues = data.filter((timeValue) => {
        return timeValue.time.day === today
      })
      const yesterdayValues = data.filter(
        (timeValue) => timeValue.time.day === yesterday
      )

      let yesterdayValue = null
      let todayOpenValue = null
      let todayCloseValue = null
      if (yesterdayValues.length > 0) {
        yesterdayValue =
          yesterdayValues.length > 1 ? yesterdayValues[1] : yesterdayValues[0]
      }

      if (todayValues.length > 0) {
        todayOpenValue =
          todayValues.length > 1 ? todayValues[1] : todayValues[0]
        todayCloseValue = todayValues.length > 1 ? todayValues[0] : null
      }

      return {
        yesterday: yesterdayValue, // take later value for closing
        todayOpen: todayOpenValue,
        todayClose: todayCloseValue,
        mostRecentValue: data[data.length - 1],
      }
    },
    async loadData() {
      let player = {}

      const newPlayerData = await this.getSinglePlayerById({
        id: this.playerId,
      })

      if (newPlayerData) {
        player = newPlayerData
      }

      await this.getMarketValue({
        playerId: this.playerId,
        season: this.season,
      })
        .then((data) => {
          const marketValue = this.transformMarketValue(data)

          this.player = {
            ...player,
            table: this.getPlayerTableData(player, marketValue),
            marketValue: marketValue,
          }
          this.marketValueData = data
        })
        .then(() => {
          this.loaded = true
        })
    },
    getPlayerTableData(player, marketValue = null) {
      const data = {
        id: player.id,
        age: {
          label: this.$t('playerAge'),
          value: getAgeFromDate(player.birth_date),
        },
        country: {
          label: this.$t('playerCountry'),
          value: player.country,
        },
        club: {
          label: this.$t('playerClub'),
          value: player.team.name,
        },
        position: {
          label: this.$t('playerPosition'),
          value: player.position,
        },
        foot: {
          label: this.$t('playerFoot'),
          value: this.$t(player.foot_name),
        },
      }

      if (marketValue !== null) {
        if (marketValue.yesterday !== null) {
          data.yesterday = {
            label:
              this.$t('market_value.playerMarketValueYesterday') +
              (marketValue.yesterday.percentage_variation !== null
                ? ' (' + marketValue.yesterday.percentage_variation + '%)'
                : ''),
            value: '€ ' + marketValue.yesterday.value.toFixed(2) + 'M',
          }
        }
      }
      return data
    },
  },
}
</script>

<template>
  <Layout>
    <template v-slot:header>
      <div :class="$style.headerCustom">
        <v-btn
          small
          download
          :class="$style.rulebookDownloadBtn"
          href="/Rulebook.pdf"
          >{{ $t('market_value.rulebook') }}</v-btn
        >

        <v-img
          src="@assets/images/csa_capital_market.png"
          alt="CSA Captial Logo"
          max-height="80"
          max-width="170"
        >
        </v-img
      ></div>
    </template>
    <div v-if="loaded && !showPlayerNotFound" :class="$style.layout">
      <v-hover v-slot="{ hover }">
        <v-card
          v-if="player"
          :link="true"
          :elevation="hover ? 12 : 2"
          :class="[{ 'on-hover': hover }, $style.topPickCard]"
          tag="article"
        >
          <v-card-title
            :class="$style.title"
            v-text="player.name"
          ></v-card-title>
          <header :class="[$style.cardHeader, 'pb-0']"> </header>

          <v-simple-table dense :class="$style.table">
            <tbody>
              <tr>
                <td :class="$style.td">
                  <div :class="$style.mainValue">
                    <v-tooltip
                      v-if="
                        player.marketValue && player.marketValue.mostRecentValue
                      "
                      top
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <h4
                          :class="$style.playerIndex"
                          :title="$t('market_value.playerMarketValue')"
                          v-bind="attrs"
                          v-on="on"
                          >€
                          {{
                            player.marketValue.mostRecentValue.value.toFixed(2)
                          }}M</h4
                        >
                      </template>
                      <span
                        >{{
                          $t('market_value.playerMarketValue') +
                            (player.marketValue.mostRecentValue
                              .percentage_variation !== null
                              ? ' (' +
                                player.marketValue.mostRecentValue
                                  .percentage_variation +
                                '%)'
                              : '')
                        }}:</span
                      >
                    </v-tooltip>
                  </div>
                </td>
                <td :class="$style.td">
                  <v-btn
                    class="mr-4"
                    small
                    aria-label="Download Data as Excel"
                    @click="downloadExcel"
                    >{{ $t('market_value.excel_download') }}</v-btn
                  >
                </td>
              </tr>
              <tr v-for="item in player.table" :key="item.name">
                <td :class="$style.td">{{ item.label }}</td>
                <td :class="$style.td">{{ item.value }}</td>
              </tr>
            </tbody>
          </v-simple-table>
          <chartMarketValue
            v-if="marketValueData"
            :player-data="marketValueData"
          />
        </v-card> </v-hover
    ></div>
    <div v-if="showPlayerNotFound">
      <h1 :class="$style.notfound">Player not available</h1>
    </div>
  </Layout>
</template>

<style lang="scss" module>
@import '@design';

.headerCustom {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.layout {
  display: flex;
  flex-direction: row;

  article {
    margin: 0.4rem;
  }
}

.playerIndex {
  @extend %typography-large;

  color: $color-accent !important;
}

.marketValueLabel {
  font-size: 10px;
}

.playerThumb {
  justify-content: center;
  width: 50px;
  height: 50px;

  @media screen and (min-width: $screen-md) {
    width: 70px;
    height: 70px;
  }
}

.cardHeader {
  position: relative;
  display: flex;
  padding: 1rem;
}

.mainValue {
  margin-right: 1rem;
}

.title {
  padding-top: 0 !important;
  padding-bottom: 0 !important;
  margin-top: 1rem;
  font-size: 1.5rem;
}

.topPickCard {
  height: 100%;
  min-height: 21rem;
  overflow: hidden;
  border-bottom: 4px solid #2996f3 !important;
}

.table {
  display: inline-block;
  margin-bottom: 2rem;
}

.td {
  height: 24px !important;
  border: 0 !important;
}

.rulebookDownloadBtn {
  @media screen and (min-width: $screen-md) {
    margin-left: 1rem;
  }
}

.notfound {
  color: #fff;
}
</style>
